import { isProductionEnvironment } from '../utils/productionCheck';
import { getServiceApiUrl } from '@airclou/utils';
import { detectAppFromPathname, getCurrentApp } from '../utils/appDetection';

/**
 * Extract the workspace-aware API base path from the current URL.
 *
 * On app.airclou.com, URLs follow the pattern /{workspaceSlug}/{app}/...
 * The API base must include the workspace slug so that nginx routes the
 * request to the correct backend, e.g. /customer-support/helpdesk/api.
 */
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (isLocalhost) {
      const port = window.location.port;
      if (port === '5174') return getServiceApiUrl('dashboard');
      if (port === '5173') return getServiceApiUrl('playbook');
      if (port === '5175') return getServiceApiUrl('helpdesk');
    } else {
      if (hostname === 'dashboard.airclou.com') {
        return getServiceApiUrl('dashboard');
      } else if (hostname === 'app.airclou.com') {
        const pathname = window.location.pathname;
        const app = detectAppFromPathname(pathname);
        if (app) {
          const segments = pathname.split('/').filter(Boolean);
          const appIndex = segments.indexOf(app);
          if (appIndex > 0) {
            const workspaceSlug = segments.slice(0, appIndex).join('/');
            return `/${workspaceSlug}/${app}/api`;
          }
          return getServiceApiUrl(app === 'auth' ? 'auth' : app as any);
        }
      }
    }
  }

  return getServiceApiUrl('playbook');
}

const getAuthToken = (): string | null => {
  try {
    const app = typeof window !== 'undefined' ? getCurrentApp() : null;

    if (app) {
      const t = localStorage.getItem(`${app}:authToken`);
      if (t) return t;
    }

    return localStorage.getItem('dashboard:authToken') ||
           localStorage.getItem('playbook:authToken') ||
           localStorage.getItem('helpdesk:authToken') ||
           null;
  } catch {
    return null;
  }
}

const getWorkspaceId = (): string => {
  try {
    return localStorage.getItem('dashboard:workspaceId') ||
           localStorage.getItem('helpdesk:workspaceId') ||
           localStorage.getItem('playbook:workspaceId') ||
           'ws_default';
  } catch {
    return 'ws_default';
  }
}

const getToken = (): string | null => {
  if (isProductionEnvironment()) {
    return getAuthToken();
  }
  return getAuthToken() || 'demo-token-for-testing';
}

export interface RecentSearch {
  query: string;
  context: string;
  lastUsed: string;
}

export interface RecentSearchResponse {
  recent_searches: RecentSearch[];
  total: number;
}

class RecentSearchService {
  private fixedBaseUrl: string | null;
  private getTokenFn: (() => string | null) | null = null;

  constructor(baseUrl?: string) {
    this.fixedBaseUrl = baseUrl || null;
  }

  private get baseUrl(): string {
    return this.fixedBaseUrl || getApiBaseUrl();
  }

  setTokenProvider(getToken: () => string | null) {
    this.getTokenFn = getToken;
  }

  /**
   * Build common headers and resolve auth token.
   * Returns null if the request should be skipped (e.g. no valid token in production).
   */
  private resolveRequestHeaders(): { headers: Record<string, string>; token: string | null } | null {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    headers['X-Workspace-ID'] = getWorkspaceId();

    const token = this.getTokenFn ? this.getTokenFn() : getToken();
    const isDemoToken = token && token.startsWith('demo-token-');

    if (isProductionEnvironment() && (!token || isDemoToken)) {
      return null;
    }

    if (isDemoToken && typeof window !== 'undefined' && window.location.port === '5175') {
      return null;
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return { headers, token };
  }

  async getRecentSearches(limit: number = 10, context: string = 'general'): Promise<RecentSearch[]> {
    try {
      const resolved = this.resolveRequestHeaders();
      if (!resolved) return [];

      const url = `${this.baseUrl}/search/recent?` + new URLSearchParams({
        limit: limit.toString(),
        context
      });

      const response = await fetch(url, { method: 'GET', headers: resolved.headers });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        return result.data.recent_searches || [];
      }
      return result.recent_searches || [];
    } catch (error) {
      console.error('Error fetching recent searches:', error);
      return [];
    }
  }

  async saveRecentSearch(query: string, context: string = 'general'): Promise<void> {
    try {
      if (!query?.trim()) return;

      const resolved = this.resolveRequestHeaders();
      if (!resolved) return;

      const response = await fetch(`${this.baseUrl}/search/recent`, {
        method: 'POST',
        headers: resolved.headers,
        body: JSON.stringify({ query: query.trim(), context }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  }

  async clearRecentSearches(context: string = 'all'): Promise<void> {
    try {
      const resolved = this.resolveRequestHeaders();
      if (!resolved) return;

      const response = await fetch(`${this.baseUrl}/search/recent?` + new URLSearchParams({ context }), {
        method: 'DELETE',
        headers: resolved.headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error clearing recent searches:', error);
      throw error;
    }
  }

  async deleteRecentSearchItem(query: string, context: string = 'general'): Promise<void> {
    try {
      if (!query?.trim()) return;

      const resolved = this.resolveRequestHeaders();
      if (!resolved) return;

      const response = await fetch(`${this.baseUrl}/search/recent/item`, {
        method: 'DELETE',
        headers: resolved.headers,
        body: JSON.stringify({ query: query.trim(), context }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting recent search item:', error);
      throw error;
    }
  }
}

export const recentSearchService = new RecentSearchService();
